import React, { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import './CfoPageShell.css'
import './CfoCockpitPage.css'
import { ServerApiClient } from '../../../data/ServerApiClient'
import type {
  FocusRatioSlice,
  InsightCard,
  InsightDrillTarget,
  QuickBooksFpaStatementRender,
  QuickBooksFpaStatementRow,
  QuickBooksCfoPresentationResponse,
  QuickBooksCfoStatementsRenderContractResponse,
  ServerFirmAttentionItem,
  ServerFirmPortfolio,
  ServerFirmPortfolioClient,
  ServerSiteMembership,
} from '../../../data/ServerApiClient'
import { selectBriefHighlights } from './visuals/briefHighlights'
import { KeyHighlightsSection } from './visuals/KeyHighlightsSection'
import { CockpitFocusRatioCell, formatFocusPeriod } from './CockpitFocusRatioCell'
import { CockpitMetricTrendCell } from './CockpitMetricTrendCell'
import { CockpitStageCell, canMarkReviewed } from './CockpitStageCell'
import { ensureApiSessionReady } from '../../../data/apiSession'
import { buildCfoRoutePath, cfoPageFromRoute, withFirmCockpitContext, type CfoPageSlug } from './cfoRoutes'
import { resolveCfoPageFromDrillTarget } from './cfoNav'
import { navigateTo, CfoBrandMark } from '../../cfo-site/CfoSiteShared'
import { deltaColor, formatDelta, scoreToGrade } from './cfoHealthLabels'
import { useCfoFirmNavigationOptional } from './CfoFirmNavigation'

type LoadStatus = 'loading' | 'ready' | 'error'
type Lens = 'book' | 'board'
type HealthFilter = 'all' | 'needs_review'
type SortBy = 'health' | 'health_score' | 'health_drop' | 'name' | 'attention' | 'focus_ratio'

const ACCOUNTING_PROVIDER_LABELS: Record<string, string> = {
  quickbooks: 'QuickBooks',
  xero: 'Xero',
  sage_intacct: 'Sage Intacct',
  sage_accounting: 'Sage Accounting',
  netsuite: 'NetSuite',
}

export function accountingProviderLabel(provider: string | null | undefined): string {
  const key = provider || 'quickbooks'
  return ACCOUNTING_PROVIDER_LABELS[key] || key
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const HEALTH_RANK: Record<string, number> = { blocked: 0, at_risk: 1, watch: 2, healthy: 3 }
const HEALTH_LABEL: Record<string, string> = {
  blocked: 'Blocked',
  at_risk: 'At risk',
  watch: 'Watch',
  healthy: 'Healthy',
}
const SEVERITY_RANK: Record<string, number> = { bad: 0, check: 1, watch: 2, good: 3 }
const STAGE_ORDER: { key: string; label: string }[] = [
  { key: 'needs_review', label: 'Needs review' },
  { key: 'reviewed', label: 'Reviewed' },
  { key: 'issued', label: 'Issued' },
  { key: 'shared', label: 'Shared' },
  { key: 'viewed', label: 'Viewed by client' },
  { key: 'stale', label: 'Stale · reconnect' },
]
const STAGE_LABEL: Record<string, string> = Object.fromEntries(STAGE_ORDER.map((s) => [s.key, s.label]))
const RATIO_CATEGORY_LABEL: Record<string, string> = {
  liquidity: 'Liquidity',
  leverage: 'Leverage',
  efficiency: 'Efficiency',
  profitability: 'Profitability',
  coverage: 'Coverage',
}

function fmtMoneyCompact(v: number | null | undefined, currency = 'USD'): string {
  if (v == null || !isFinite(v)) return '—'
  return new Intl.NumberFormat(undefined, {
    style: 'currency', currency: currency || 'USD', notation: 'compact',
    maximumFractionDigits: Math.abs(v) >= 1_000_000 ? 1 : 0,
  }).format(v)
}
function fmtPct(v: number | null | undefined): string {
  if (v == null || !isFinite(v)) return '—'
  // Portfolio-summary percentages are stored as fractions (0.427 = 42.7%);
  // rendering the raw value made every margin read "0.4%".
  return `${(v * 100).toFixed(1)}%`
}
function relTime(iso: string | null | undefined): string {
  if (!iso) return 'not analyzed yet'
  const then = Date.parse(iso)
  if (!isFinite(then)) return 'not analyzed yet'
  const mins = Math.round((Date.now() - then) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.round(hrs / 24)
  return `${days}d ago`
}
function revenueMomPct(c: ServerFirmPortfolioClient): number | null {
  if (c.revenue == null || c.revenue_prior == null || c.revenue_prior === 0) return null
  return ((c.revenue - c.revenue_prior) / Math.abs(c.revenue_prior)) * 100
}

/** The ratio the focus column starts on — the metric this column showed before it was selectable. */
const DEFAULT_FOCUS_RATIO_ID = 'days_sales_outstanding'

/** Per-firm, per-browser. The cockpit is site-scoped and the workbook prefs table is keyed on
 *  workbook with no user column, so there is no server home for a firm-wide personal pick. */
const focusRatioStorageKey = (siteSlug: string) =>
  `fg:cfo-core:focus-ratio:${siteSlug || 'default'}`

export function readFocusRatio(siteSlug: string): string {
  try {
    return window.localStorage.getItem(focusRatioStorageKey(siteSlug)) || DEFAULT_FOCUS_RATIO_ID
  } catch {
    // Storage is unavailable in private mode / hardened profiles; the default still works.
    return DEFAULT_FOCUS_RATIO_ID
  }
}

function writeFocusRatio(siteSlug: string, ratioId: string) {
  try {
    window.localStorage.setItem(focusRatioStorageKey(siteSlug), ratioId)
  } catch {
    // Non-fatal: the selection simply doesn't survive this reload.
  }
}

/** Gross margin's monthly series, reused from the focus-ratio pack — it is a canonical ratio, so no
 *  separate column exists or is needed. Values are fractions (0.539 = 53.9%). */
export function marginTrend(c: ServerFirmPortfolioClient): { period: string; value: number }[] {
  const slice = (c.focus_ratios ?? []).find((s) => s.id === 'gross_margin')
  return (slice?.trend ?? []).filter((p) => p?.value != null && Number.isFinite(p.value))
}

/** Margin's month-over-month move in PERCENTAGE POINTS, so "1.4 pts" not "1.4%" of a percentage. */
export function marginMomPts(c: ServerFirmPortfolioClient): number | null {
  const trend = marginTrend(c)
  if (trend.length < 2) return null
  return (trend[trend.length - 1].value - trend[trend.length - 2].value) * 100
}

/** The focused ratio's slice for one client, or null when that client's pack has no such entry. */
export function focusSliceFor(
  c: ServerFirmPortfolioClient,
  ratioId: string,
): FocusRatioSlice | null {
  return (c.focus_ratios ?? []).find((s) => s.id === ratioId) ?? null
}

/**
 * The ratios offered in the selector: the union across every client in the book, so a firm sees a
 * ratio as soon as any one client reports it, ordered by category then first-seen. The default
 * always appears even before any pack has landed, so the control is never empty on a cold book.
 */
export function focusRatioOptions(
  clients: ServerFirmPortfolioClient[],
): { id: string; label: string; category: string }[] {
  const seen = new Map<string, { id: string; label: string; category: string }>()
  for (const c of clients) {
    for (const s of c.focus_ratios ?? []) {
      if (!seen.has(s.id)) seen.set(s.id, { id: s.id, label: s.label, category: s.category })
    }
  }
  if (!seen.size) {
    return [{ id: DEFAULT_FOCUS_RATIO_ID, label: 'Days Sales Outstanding', category: 'efficiency' }]
  }
  const order = ['liquidity', 'leverage', 'efficiency', 'profitability', 'coverage']
  return [...seen.values()].sort((a, b) => {
    const ca = order.indexOf(a.category)
    const cb = order.indexOf(b.category)
    if (ca !== cb) return (ca < 0 ? order.length : ca) - (cb < 0 ? order.length : cb)
    return a.label.localeCompare(b.label)
  })
}

/** Fallback color band for the health gauge when a client has no score yet —
 * drawn from the categorical `health` so an unanalyzed row still reads sanely. */
const HEALTH_BAND: Record<string, 'good' | 'watch' | 'risk' | 'none'> = {
  healthy: 'good',
  watch: 'watch',
  at_risk: 'risk',
  blocked: 'none',
}

/** Band for a scored client, keyed to the LETTER GRADE the tile displays —
 * A reads teal, B amber, C and below red. Deliberately not the categorical
 * health: a B- client with one bad flag was rendering a red "B-", which read
 * as a contradiction. The attention pill still carries flag urgency. */
function gradeBand(score: number): 'good' | 'watch' | 'risk' {
  const grade = scoreToGrade(score)
  if (grade.startsWith('A')) return 'good'
  if (grade.startsWith('B')) return 'watch'
  return 'risk'
}

/** Friendly destination noun for a drawer item's deep link, derived from its
 * drill target — "Open cash →", "Open P&L →", "Open A/R →" — so each issue reads
 * as coming from its own surface, the way the roster's Open lands on one. */
export function drillDestinationLabel(
  drill: InsightDrillTarget | null | undefined,
): string | null {
  const resolved = resolveCfoPageFromDrillTarget(drill)
  if (!resolved) return null
  switch (resolved.pageId) {
    case 'financials': {
      const view = drill?.view ? String(drill.view) : null
      if (view === 'balance_sheet') return 'balance sheet'
      if (view === 'cash_flow') return 'cash flow'
      return 'P&L'
    }
    case 'cash':
      return 'cash'
    case 'ar':
      return 'A/R'
    case 'ratios':
      return 'ratios'
    case 'valuation':
      return 'valuation'
    case 'forecast':
    case 'scenarios':
      return 'forecast'
    case 'gl_detail':
      return 'GL'
    default:
      return null
  }
}

/** Map live insight cards (the same ranked spine the Brief renders) to drawer
 * items — the shape the server also denormalizes into `attention_items`. */
export function itemsFromInsightCards(cards: InsightCard[]): ServerFirmAttentionItem[] {
  return cards
    .filter((card) => card.severity !== 'good')
    .slice(0, 4)
    .map((card) => ({
      severity: card.severity,
      headline: card.headline,
      detail: card.body?.trim() ? card.body : card.value_display ?? null,
      category: card.category ?? null,
      drill: card.drill_target,
    }))
}

/** The ranked issues shown in a client's drawer. Prefers the server's multi-item
 * payload; during the migration window (before a client's Home re-renders) it
 * synthesizes a single item from the denormalized top flag so a client with a
 * known problem still opens a useful drawer. */
function attentionItemsFor(c: ServerFirmPortfolioClient): ServerFirmAttentionItem[] {
  const items = Array.isArray(c.attention_items) ? c.attention_items : []
  if (items.length > 0) return items
  if (c.top_flag_headline && (SEVERITY_RANK[c.top_flag_severity || 'good'] ?? 3) < 3) {
    const headline = c.attention_reason || c.top_flag_headline
    return [
      {
        severity: c.top_flag_severity ?? null,
        headline,
        detail: headline !== c.top_flag_headline ? c.top_flag_headline : null,
        drill: (c.health_attention_drill || c.top_flag_drill) as ServerFirmAttentionItem['drill'],
      },
    ]
  }
  return []
}

const finiteStatementValue = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

/** Portfolio `period` and statement `as_of` are month ids (`YYYY-MM-01`), not
 * through-dates. Convert them before the shared selector decides whether the
 * period is still posting and before the card prints its governed watermark. */
function cockpitDataThrough(value: string | null | undefined): string | null {
  const match = /^(\d{4})-(\d{2})-01$/.exec(value || '')
  if (!match) return value || null
  const year = Number(match[1])
  const month = Number(match[2])
  const now = new Date()
  const day = year === now.getUTCFullYear() && month === now.getUTCMonth() + 1
    ? now.getUTCDate()
    : new Date(Date.UTC(year, month, 0)).getUTCDate()
  return `${match[1]}-${match[2]}-${String(day).padStart(2, '0')}`
}

const COCKPIT_PRESENTATION_CACHE_MS = 5 * 60 * 1000
type CockpitPresentationCacheEntry = {
  loadedAt?: number
  value?: QuickBooksCfoPresentationResponse
  promise?: Promise<QuickBooksCfoPresentationResponse>
}
const cockpitPresentationCache = new WeakMap<
  ServerApiClient,
  Map<string, CockpitPresentationCacheEntry>
>()

/**
 * Join and briefly cache the expensive full evidence package.
 *
 * The statement contract paints the first visual quickly. This request then
 * unlocks the rest of the governed visual library without blocking first paint.
 * Joining in-flight opens and retaining a short response cache prevents row
 * close/reopen gestures from repeatedly assembling the same board package.
 */
export function loadCockpitPresentationEvidence(
  api: ServerApiClient,
  workbookId: string,
  accountingProvider?: string | null,
): Promise<QuickBooksCfoPresentationResponse> {
  let entries = cockpitPresentationCache.get(api)
  if (!entries) {
    entries = new Map()
    cockpitPresentationCache.set(api, entries)
  }
  const key = `${workbookId}:${accountingProvider || ''}`
  const existing = entries.get(key)
  if (existing?.promise) return existing.promise
  if (existing?.value && existing.loadedAt && Date.now() - existing.loadedAt < COCKPIT_PRESENTATION_CACHE_MS) {
    return Promise.resolve(existing.value)
  }
  const request = api.getCfoPresentation(workbookId, {
    // The full library needs A/R aging, concentration, and collection evidence.
    // This runs after the statement visual is already visible.
    includeArAnalysis: true,
    knownProvider: accountingProvider || null,
  }).then((value) => {
    entries!.set(key, { value, loadedAt: Date.now() })
    return value
  }).catch((error) => {
    entries!.delete(key)
    throw error
  })
  entries.set(key, { promise: request })
  return request
}

/**
 * Build the cockpit's fast first visual from the statement contract.
 *
 * This is intentionally only the first-paint model. CockpitClientHighlight
 * upgrades it with the complete presentation evidence package after it is on
 * screen, so fast expansion no longer means permanently restricting the user
 * to statement-derived charts.
 */
export function buildCockpitHighlights(
  response: QuickBooksCfoStatementsRenderContractResponse,
  reportingPeriod?: string | null,
): { presentation: QuickBooksCfoPresentationResponse; card: InsightCard; cards: InsightCard[] } | null {
  const statement = response.profit_and_loss
  if (!statement || statement.periods.length < 2) return null
  const rowFor = (
    source: QuickBooksFpaStatementRender | null | undefined,
    ids: string[],
    labels: RegExp,
  ): QuickBooksFpaStatementRow | null => source?.rows.find((candidate) =>
    ids.includes(String(candidate.node_id || ''))
    || ids.includes(String(candidate.semantic_key || ''))
    || labels.test(String(candidate.label || '')),
  ) || null
  const movementCard = (args: {
    row: QuickBooksFpaStatementRow | null
    source: QuickBooksFpaStatementRender | null | undefined
    metricId: string
    label: string
    detector: string
    category: string
    favourableWhenUp: boolean
    rank: number
    drillTarget: InsightDrillTarget
  }): InsightCard | null => {
    const { row, source } = args
    if (!row || !source || source.periods.length < 2) return null
    const latestIndex = source.periods.length - 1
    const priorIndex = latestIndex - 1
    const current = finiteStatementValue(row.values[latestIndex])
    const prior = finiteStatementValue(row.values[priorIndex])
    if (current == null || prior == null) return null
    const delta = current - prior
    const pct = Math.abs(prior) > 0.01 ? delta / Math.abs(prior) : null
    const direction = delta >= 0 ? 'up' : 'down'
    const verb = delta > 0 ? 'rose' : delta < 0 ? 'fell' : 'held steady'
    const pctText = pct == null || delta === 0 ? '' : ` ${Math.abs(pct * 100).toFixed(0)}%`
    const favorable = delta === 0 || (args.favourableWhenUp ? delta > 0 : delta < 0)
    return {
      id: `cockpit:${args.metricId}:mom`,
      detector: args.detector,
      severity: favorable ? 'good' : 'watch',
      headline: `${args.label} ${verb}${pctText} since last month`,
      body: `${args.label} moved by ${fmtMoneyCompact(delta)}, from ${fmtMoneyCompact(prior)} to ${fmtMoneyCompact(current)}.`,
      impact: { value: delta, unit: 'usd', metric_id: args.metricId, comparison: 'mom', direction },
      drill_target: args.drillTarget,
      score: Math.max(Math.abs(pct || 0), Math.log10(1 + Math.abs(delta)) / 10),
      rank: args.rank,
      // An improving current priority is still material. Omitting these tags
      // was the direct cause of the empty "did not meet the evidence bar"
      // section on healthy demo companies.
      tags: ['current_priority', ...(favorable ? ['favorable', 'opportunity'] : ['watch'])],
      category: args.category,
      trend: source.periods.flatMap((period, index) => {
        const value = finiteStatementValue(row.values[index])
        return value == null ? [] : [{ period, value }]
      }),
      value_display: fmtMoneyCompact(delta),
    }
  }

  // The cockpit used to manufacture only one Net Income card, which guaranteed
  // the same waterfall for every company. Keep the fast statement-only request,
  // but give the shared selector a small governed candidate set: the principal
  // P&L movements plus cash. It can now choose the most important story and a
  // genuinely independent second visual when the evidence supports one.
  const candidates = [
    movementCard({
      row: rowFor(statement, ['fpa:pnl:net_income'], /^net income$/i), source: statement,
      metricId: 'net_income', label: 'Net Income', detector: 'income_bridge', category: 'income_bridge', favourableWhenUp: true, rank: 1,
      drillTarget: { tab: 'financials', view: 'profit_and_loss', params: { metric_id: 'net_income', comparison: 'mom' } },
    }),
    movementCard({
      row: rowFor(statement, ['fpa:pnl:total_revenue'], /^(total )?(income|revenue)$/i), source: statement,
      metricId: 'revenue', label: 'Total Revenue', detector: 'period_variance', category: 'revenue', favourableWhenUp: true, rank: 2,
      drillTarget: { tab: 'financials', view: 'profit_and_loss', params: { metric_id: 'revenue', comparison: 'mom' } },
    }),
    movementCard({
      row: rowFor(statement, ['fpa:pnl:total_expenses'], /^(total |operating )?expenses$/i), source: statement,
      metricId: 'operating_expenses', label: 'Operating Expenses', detector: 'period_variance', category: 'expense', favourableWhenUp: false, rank: 3,
      drillTarget: { tab: 'financials', view: 'profit_and_loss', params: { metric_id: 'operating_expenses', comparison: 'mom' } },
    }),
    movementCard({
      row: rowFor(response.balance_sheet, ['cash_and_cash_equivalents', 'cash'], /cash( and cash equivalents)?$/i), source: response.balance_sheet,
      metricId: 'cash', label: 'Cash', detector: 'cash_runway', category: 'cash', favourableWhenUp: true, rank: 4,
      drillTarget: { tab: 'cash', view: null, params: { metric_id: 'cash', comparison: 'mom' } },
    }),
  ].filter((candidate): candidate is InsightCard => Boolean(candidate))
  if (!candidates.length) return null
  const presentation = {
    workbook_id: response.workbook_id,
    binding_id: '',
    connection_id: response.connection_id,
    layout_version_id: statement.layout_version_id,
    layout_name: '',
    can_edit: false,
    config: null,
    metrics: null,
    ar_analysis: null,
    statements: {
      profit_and_loss: response.profit_and_loss,
      balance_sheet: response.balance_sheet,
      cash_flow: response.cash_flow,
    },
    warnings: response.warnings,
    source_state: {
      signature: response.contract.freshness_token,
      data_through: cockpitDataThrough(reportingPeriod || response.as_of),
      reconciliation_status: response.contract.readiness === 'ready' ? 'passed' : response.contract.readiness,
      new_data_available: response.contract.readiness === 'stale',
    },
    insights: {
      workbook_id: response.workbook_id,
      connection_id: response.connection_id,
      realm_id: response.realm_id,
      as_of: response.as_of,
      grain: response.grain,
      insight_set_version: 'cockpit-statement-v1',
      signature: response.contract.freshness_token,
      attention_count: candidates.filter((candidate) => candidate.severity !== 'good').length,
      cards: candidates,
      warnings: response.warnings,
    },
    metric_comparisons: null,
    generated_at: response.contract.generated_at,
  } as QuickBooksCfoPresentationResponse
  return { presentation, card: candidates[0], cards: candidates }
}


/**
 * Key Highlights for ONE expanded client.
 *
 * The cockpit is a firm roster: it holds no per-workbook payload, and fetching
 * one per client would mean N requests to paint a page. So this fetches only
 * when a row is actually opened, and only for that row — the drawer is already
 * the "I am looking at this client" gesture.
 *
 * Runs the same registry, evidence builder and selector as the brief and the
 * board pack, so all three agree about what matters for a given client.
 */
function CockpitClientHighlight({
  api,
  workbookId,
  accountingProvider,
  reportingPeriod,
}: {
  api: ServerApiClient
  workbookId: string
  accountingProvider?: string | null
  reportingPeriod?: string | null
}): ReactNode {
  const [presentation, setPresentation] = useState<QuickBooksCfoPresentationResponse | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'unavailable'>('loading')

  useEffect(() => {
    let cancelled = false
    // Degrade rather than throw when the client cannot serve this. The cockpit
    // is a roster: a missing visual must never take the row down with it.
    if (typeof api?.getCfoStatementsRender !== 'function') {
      setState('unavailable')
      return
    }
    setState('loading')
    setPresentation(null)
    const statementOptions = {
      knownProvider: accountingProvider || 'quickbooks',
      ...(reportingPeriod ? { asOf: reportingPeriod } : {}),
    }
    const upgradeToFullEvidence = (fallbackAvailable: boolean) => {
      if (typeof api?.getCfoPresentation !== 'function') {
        if (!fallbackAvailable && !cancelled) setState('unavailable')
        return
      }
      void loadCockpitPresentationEvidence(api, workbookId, accountingProvider)
        .then((full) => {
          if (cancelled) return
          setPresentation(full)
          setState('ready')
        })
        .catch(() => {
          // A failed enrichment must not remove the fast governed visual.
          if (!fallbackAvailable && !cancelled) setState('unavailable')
        })
    }
    void api
      .getCfoStatementsRender(workbookId, statementOptions)
      .then((next) => {
        if (cancelled) return
        const built = buildCockpitHighlights(next, reportingPeriod)
        setPresentation(built?.presentation || null)
        if (built) setState('ready')
        // Upgrade in place after the fast statement-backed visual has painted.
        // A company with no qualifying statement card still gets the full
        // library rather than falling into an empty drawer.
        upgradeToFullEvidence(Boolean(built))
      })
      .catch(() => upgradeToFullEvidence(false))
    return () => { cancelled = true }
  }, [accountingProvider, api, reportingPeriod, workbookId])

  const selection = useMemo(() => {
    if (!presentation) return null
    const selected = selectBriefHighlights({
      insights: presentation.insights || null,
      comparisons: presentation.metric_comparisons || null,
      presentation,
      dataThrough: presentation.source_state?.data_through || null,
      // The library owns 27 visual grammars; the selector chooses at most three
      // evidence-distinct stories for this company rather than dumping a chart
      // gallery into a roster row.
      limit: 3,
      ensureCurrentPriorities: true,
    })
    if (selected.highlights.length || !presentation.key_highlights?.length) return selected
    // Older served packages can already carry a governed selection even when
    // their insight candidate spine is absent. Preserve that useful visual
    // instead of replacing it with an empty-state diagnostic.
    return {
      ...selected,
      highlights: presentation.key_highlights.slice(0, 3),
      evidence: presentation.visual_evidence || [],
      rejections: presentation.visual_selection_diagnostics?.rejections || selected.rejections,
      visualSelectionVersion: presentation.visual_selection_version || selected.visualSelectionVersion,
      visualEvidenceSchemaVersion: presentation.visual_evidence_schema_version || selected.visualEvidenceSchemaVersion,
    }
  }, [presentation])

  if (state === 'loading') return <div className="ai-loading">Loading the visual…</div>
  if (state === 'unavailable' || !selection) return null
  return (
    <KeyHighlightsSection
      highlights={selection.highlights}
      evidence={selection.evidence}
      rejections={selection.rejections}
    />
  )
}


export type CfoCockpitPageProps = {
  topInsetPx?: number
  embedded?: boolean
  headerActions?: ReactNode
  selectedWorkbookId?: string | null
  onCompanySelect?: (client: ServerFirmPortfolioClient) => void
}

export function CfoCockpitPage({
  topInsetPx,
  embedded = false,
  headerActions,
  selectedWorkbookId = null,
  onCompanySelect,
}: CfoCockpitPageProps) {
  const localApi = useMemo(() => new ServerApiClient(), [])
  const firmNavigation = useCfoFirmNavigationOptional()
  const hasFirmNavigation = Boolean(firmNavigation)
  const api = firmNavigation?.api || localApi
  const siteSlug = useMemo(
    () => firmNavigation?.siteSlug || window.location.pathname.split('/').filter(Boolean)[0] || '',
    [firmNavigation?.siteSlug],
  )
  const [status, setStatus] = useState<LoadStatus>('loading')
  const [error, setError] = useState<string | null>(null)
  const selectedRowRef = useRef<HTMLDivElement | null>(null)
  const [data, setData] = useState<ServerFirmPortfolio | null>(null)
  const [members, setMembers] = useState<ServerSiteMembership[]>([])

  const [lens, setLens] = useState<Lens>('book')
  const [healthFilter, setHealthFilter] = useState<HealthFilter>('all')
  const [providerFilter, setProviderFilter] = useState<string>('all')
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all')
  const [sortBy, setSortBy] = useState<SortBy>('health')
  // The one ratio the whole roster is focused on. Defaults to DSO so the cockpit opens on exactly
  // the column it carried before this control existed.
  const [focusRatioId, setFocusRatioId] = useState<string>(() => readFocusRatio(siteSlug))
  // Workbook id currently being marked reviewed, and the note shown when the server refuses.
  const [reviewing, setReviewing] = useState<string | null>(null)
  const [reviewNote, setReviewNote] = useState<string | null>(null)
  // Board lens: the card being dragged, and the column currently under it.
  const [dragging, setDragging] = useState<string | null>(null)
  const [dropTarget, setDropTarget] = useState<string | null>(null)
  // Which client rows have their attention drawer open (by workbook id).
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  // Live-fetched drawer items per workbook. The portfolio row's denormalized
  // `attention_items` only refreshes when that client's Home re-renders, so an
  // opened drawer whose row is stale (fewer items than the client's real
  // attention_count) pulls the ranked insight spine directly and renders the
  // full multi-surface list. 'loading' keeps the fallback visible with a hint;
  // 'error' silently keeps the fallback.
  const [liveItems, setLiveItems] = useState<
    Record<string, ServerFirmAttentionItem[] | 'loading' | 'error'>
  >({})

  const fetchLiveItems = (c: ServerFirmPortfolioClient) => {
    setLiveItems((prev) => ({ ...prev, [c.workbook_id]: 'loading' }))
    api
      .getCfoInsights(c.workbook_id)
      .then((res) => {
        setLiveItems((prev) => ({
          ...prev,
          [c.workbook_id]: itemsFromInsightCards(res.cards ?? []),
        }))
      })
      .catch(() => {
        setLiveItems((prev) => ({ ...prev, [c.workbook_id]: 'error' }))
      })
  }

  // A drawer needs the live fill-in when the summary row carries fewer items
  // than the client claims to have (attention_count counts non-good cards).
  const needsLiveItems = (c: ServerFirmPortfolioClient) => {
    const have = attentionItemsFor(c).length
    const want = Math.min(Math.max(c.attention_count || 0, 0), 4)
    return c.last_analyzed_at != null && have < want && liveItems[c.workbook_id] === undefined
  }

  const toggleExpanded = (c: ServerFirmPortfolioClient) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(c.workbook_id)) next.delete(c.workbook_id)
      else {
        next.add(c.workbook_id)
        if (needsLiveItems(c)) fetchLiveItems(c)
      }
      return next
    })

  useEffect(() => {
    if (!firmNavigation) return
    setData(firmNavigation.portfolio)
    setStatus(firmNavigation.status)
    setError(firmNavigation.error)
  }, [firmNavigation])

  useEffect(() => {
    if (firmNavigation) return
    let cancelled = false
    ;(async () => {
      try {
        await ensureApiSessionReady()
        const portfolio = await api.getFirmPortfolio(siteSlug)
        if (cancelled) return
        setData(portfolio)
        setStatus('ready')
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : String(err))
        setStatus('error')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [api, firmNavigation, hasFirmNavigation, siteSlug])

  useEffect(() => {
    let cancelled = false
    void api.listSiteUsers(siteSlug)
      .then((siteUsers) => { if (!cancelled) setMembers(siteUsers) })
      .catch(() => { if (!cancelled) setMembers([] as ServerSiteMembership[]) })
    return () => { cancelled = true }
  }, [api, siteSlug])

  const selectCompany = (client: ServerFirmPortfolioClient) => {
    firmNavigation?.selectWorkbook(client.workbook_id)
    onCompanySelect?.(client)
  }
  const companySelectionEnabled = Boolean(firmNavigation || onCompanySelect)

  // Deep-link into a client's CFO Core at a specific drill target. `fallbackText`
  // lets a ratio be resolved by its label during the migration window, when a
  // persisted target predates the stable metric-id column.
  const openClientTarget = (
    c: ServerFirmPortfolioClient,
    target: InsightDrillTarget | null | undefined,
    fallbackText?: string | null,
  ) => {
    selectCompany(c)
    const resolved = resolveCfoPageFromDrillTarget(target)
    const page = resolved
      ? cfoPageFromRoute(resolved)
      : fallbackText
        ? 'ratios'
        : ((target?.tab as CfoPageSlug | undefined) || 'home')
    const params =
      target?.params && typeof target.params === 'object'
        ? (target.params as Record<string, unknown>)
        : null
    const metricId =
      typeof params?.ratio_id === 'string'
        ? params.ratio_id
        : typeof params?.metric_id === 'string'
          ? params.metric_id
          : null
    const path = buildCfoRoutePath({ siteSlug: c.site_slug, workbookId: c.workbook_id, page })
    navigateTo(withFirmCockpitContext(path, {
      metricId,
      text: !metricId ? fallbackText ?? null : null,
    }))
  }

  // The board report is the cockpit's primary delivery surface. It opens live;
  // no export or AI generation occurs on this path.
  /**
   * The one close-stage transition the server can actually perform: post a review checkpoint against
   * the signature the roster is already showing. A 409 here is meaningful rather than an error —
   * it means the books moved since this row was computed, so the message is surfaced verbatim.
   */
  const markReviewed = async (c: ServerFirmPortfolioClient) => {
    const signature = c.live_board_report?.source_signature
    if (!signature || reviewing) return
    setReviewing(c.workbook_id)
    setReviewNote(null)
    try {
      await api.createCfoReviewCheckpoint(
        c.workbook_id,
        {
          source_signature: signature,
          reporting_period: c.live_board_report?.reporting_period ?? null,
        },
        c.accounting_provider,
      )
      // Stage is derived, so there is nothing local to flip — refetch and let the server say.
      const fresh = firmNavigation
        ? await firmNavigation.refreshPortfolio()
        : await api.getFirmPortfolio(siteSlug)
      if (fresh) setData(fresh)
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setReviewNote(
        /409|governed data/i.test(message)
          ? `${c.client_name}: the books changed since this row was built. Reload, then mark it reviewed.`
          : `${c.client_name}: could not mark reviewed. ${message}`,
      )
    } finally {
      setReviewing(null)
    }
  }

  const openClient = (c: ServerFirmPortfolioClient) => {
    selectCompany(c)
    const path = c.live_board_report?.authenticated_route
      || buildCfoRoutePath({ siteSlug: c.site_slug, workbookId: c.workbook_id, page: 'presentations' })
    navigateTo(withFirmCockpitContext(path))
  }

  const openFullCfoCore = (c: ServerFirmPortfolioClient) =>
    openClientTarget(c, c.top_flag_drill as InsightDrillTarget | null | undefined)

  // A drawer item lands on that specific issue's page.
  const openAttentionItem = (c: ServerFirmPortfolioClient, item: ServerFirmAttentionItem) =>
    openClientTarget(
      c,
      (item.drill ?? null) as InsightDrillTarget | null | undefined,
      item.headline ?? null,
    )

  const assign = async (workbookId: string, userId: string | null) => {
    const email = userId ? members.find((m) => m.user_id === userId)?.email ?? null : null
    setData((prev) =>
      prev
        ? {
            ...prev,
            clients: prev.clients.map((c) =>
              c.workbook_id === workbookId
                ? { ...c, assigned_to_user_id: userId, assigned_to_email: email }
                : c,
            ),
          }
        : prev,
    )
    try {
      await api.assignFirmClient(siteSlug, workbookId, userId)
    } catch {
      // Reconcile with the server on failure.
      try {
        const fresh = firmNavigation
          ? await firmNavigation.refreshPortfolio()
          : await api.getFirmPortfolio(siteSlug)
        if (fresh) setData(fresh)
      } catch {
        /* leave optimistic state */
      }
    }
  }

  const clients = useMemo(() => data?.clients ?? [], [data?.clients])

  const focusOptions = useMemo(() => focusRatioOptions(clients), [clients])
  // A stored pick can outlive the ratio that produced it (a metric the AI demoted, a book whose
  // clients all dropped it). Fall back to whatever the book does offer rather than rendering a
  // column of blanks against a selection nothing can satisfy.
  const resolvedFocusId = useMemo(() => {
    if (focusOptions.some((o) => o.id === focusRatioId)) return focusRatioId
    return focusOptions.find((o) => o.id === DEFAULT_FOCUS_RATIO_ID)?.id ?? focusOptions[0]?.id ?? focusRatioId
  }, [focusOptions, focusRatioId])
  const focusLabel =
    focusOptions.find((o) => o.id === resolvedFocusId)?.label ?? 'Focus ratio'
  const providerOptions = useMemo(
    () => [...new Set(clients.map((c) => c.accounting_provider || 'quickbooks'))]
      .sort((a, b) => accountingProviderLabel(a).localeCompare(accountingProviderLabel(b))),
    [clients],
  )

  const filtered = useMemo(() => {
    let list = clients
    if (healthFilter === 'needs_review') list = list.filter((c) => c.health !== 'healthy')
    if (providerFilter !== 'all') {
      list = list.filter((c) => (c.accounting_provider || 'quickbooks') === providerFilter)
    }
    if (assigneeFilter === 'unassigned') list = list.filter((c) => !c.assigned_to_user_id)
    else if (assigneeFilter !== 'all')
      list = list.filter((c) => c.assigned_to_user_id === assigneeFilter)
    const sorted = [...list]
    sorted.sort((a, b) => {
      if (sortBy === 'name') return a.client_name.localeCompare(b.client_name)
      if (sortBy === 'attention') return (b.attention_count || 0) - (a.attention_count || 0)
      if (sortBy === 'health_score') {
        // Worst AI-weighted health first; clients with no score sort last.
        return (a.health_score ?? Infinity) - (b.health_score ?? Infinity)
      }
      if (sortBy === 'health_drop') {
        // Biggest month-over-month drop first (most negative delta); no-score clients last.
        return (a.health_delta_1mo ?? Infinity) - (b.health_delta_1mo ?? Infinity)
      }
      if (sortBy === 'focus_ratio') {
        // Worst-first on the focused ratio, like every other sort here — which means ascending
        // when higher is better and descending when it isn't. Clients missing the ratio sort last.
        const sa = focusSliceFor(a, resolvedFocusId)
        const sb = focusSliceFor(b, resolvedFocusId)
        const va = sa?.latest
        const vb = sb?.latest
        if (va == null || !Number.isFinite(va)) return vb == null ? 0 : 1
        if (vb == null || !Number.isFinite(vb)) return -1
        // Non-directional ratios (DPO) have no worst end; order them ascending so the sort is at
        // least stable and readable rather than implying a judgement the metric doesn't carry.
        const higherIsBetter = sa?.higher_is_better ?? sb?.higher_is_better
        return higherIsBetter === false ? vb - va : va - vb
      }
      const ha = HEALTH_RANK[a.health] ?? 9
      const hb = HEALTH_RANK[b.health] ?? 9
      if (ha !== hb) return ha - hb
      return (b.attention_count || 0) - (a.attention_count || 0)
    })
    return sorted
  }, [clients, healthFilter, providerFilter, assigneeFilter, sortBy, resolvedFocusId])

  // Rows worth auto-opening when the user hits "Expand all" — those that actually
  // carry drawer items. Computed over the filtered set so the toggle matches what's
  // on screen.
  const expandable = useMemo(
    () => filtered.filter((c) => attentionItemsFor(c).length > 0),
    [filtered],
  )
  const expandableIds = useMemo(() => expandable.map((c) => c.workbook_id), [expandable])
  const allExpanded = expandableIds.length > 0 && expandableIds.every((id) => expanded.has(id))
  const toggleExpandAll = () => {
    if (!allExpanded) expandable.filter(needsLiveItems).forEach(fetchLiveItems)
    setExpanded(allExpanded ? new Set() : new Set(expandableIds))
  }

  // The firm switcher can remember a company below the fold. Once the roster
  // mounts (and whenever the switcher changes), reveal that row inside the
  // roster scroller so the visible list agrees with the selected company.
  useEffect(() => {
    if (lens !== 'book' || !selectedWorkbookId) return
    const row = selectedRowRef.current
    if (!row || typeof row.scrollIntoView !== 'function') return
    row.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [filtered, lens, selectedWorkbookId])

  if (status === 'loading') {
    return (
      <div className={`${embedded ? '' : 'cfo-shell '}cfo-cockpit${embedded ? ' cfo-cockpit-embedded' : ''}`} style={{ paddingTop: topInsetPx || 0 }}>
        <div className="cfo-cockpit-empty">Loading your book…</div>
      </div>
    )
  }
  if (status === 'error') {
    return (
      <div className={`${embedded ? '' : 'cfo-shell '}cfo-cockpit${embedded ? ' cfo-cockpit-embedded' : ''}`} style={{ paddingTop: topInsetPx || 0 }}>
        <div className="cfo-cockpit-empty">
          Couldn’t load the firm cockpit. {error}
        </div>
      </div>
    )
  }

  const strip = data?.summary
  return (
    <div className={`${embedded ? '' : 'cfo-shell '}cfo-cockpit${embedded ? ' cfo-cockpit-embedded' : ''}`} style={{ paddingTop: topInsetPx || 0 }} data-fg-tour="firm.page">
     <div className="cockpit-inner">
      {/* ---- header ---- */}
      <header className="cockpit-head">
        <div className="cockpit-head-brand">
          <CfoBrandMark sub="Firm cockpit" />
          <div>
            <div className="cockpit-firm">{data?.site_name || 'Firm'}</div>
            <div className="cockpit-sub">
              Firm cockpit · {strip?.client_count ?? 0} client{strip?.client_count === 1 ? '' : 's'}
            </div>
          </div>
        </div>
        <div className="cockpit-head-right">
          {headerActions ? <div className="cockpit-head-actions">{headerActions}</div> : null}
          <div className="cockpit-strip" data-fg-tour="firm.stats">
            <div className="cs">
              <div className="cs-n">{strip?.client_count ?? 0}</div>
              <div className="cs-l">Clients</div>
            </div>
            <div className="cs warn">
              <div className="cs-n">{strip?.need_review ?? 0}</div>
              <div className="cs-l">Need review</div>
            </div>
            <div className="cs good">
              <div className="cs-n">{strip?.closes_ready ?? 0}</div>
              <div className="cs-l">Closes ready</div>
            </div>
            <div className="cs">
              <div className="cs-n">{strip?.sent_and_viewed ?? 0}</div>
              <div className="cs-l">Sent · viewed</div>
            </div>
            {data?.can_manage_billing && data.clients[0]?.workbook_id ? (
              <button
                type="button"
                className="btn ghost"
                onClick={() => navigateTo(`/billing/partner?workbook_id=${encodeURIComponent(data.clients[0].workbook_id)}`)}
              >
                Partner billing
              </button>
            ) : null}
          </div>
        </div>
      </header>

      {/* ---- controls ---- */}
      <div className="cockpit-controls">
        <div className="seg">
          <button
            className={lens === 'book' ? 'on' : ''}
            onClick={() => setLens('book')}
            data-fg-tour="firm.book-tab"
          >
            Book
          </button>
          <button
            className={lens === 'board' ? 'on' : ''}
            onClick={() => setLens('board')}
            data-fg-tour="firm.close-board-tab"
          >
            Close board
          </button>
        </div>
        <div className="ctrl-spacer" />
        <label className="ctrl">
          Show
          <select value={healthFilter} onChange={(e) => setHealthFilter(e.target.value as HealthFilter)}>
            <option value="all">All clients</option>
            <option value="needs_review">Needs review</option>
          </select>
        </label>
        {providerOptions.length > 1 && (
          <label className="ctrl">
            Provider
            <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)}>
              <option value="all">All providers</option>
              {providerOptions.map((provider) => (
                <option key={provider} value={provider}>
                  {accountingProviderLabel(provider)}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="ctrl">
          Owner
          <select value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)}>
            <option value="all">Anyone</option>
            <option value="unassigned">Unassigned</option>
            {members.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.email}
              </option>
            ))}
          </select>
        </label>
        {lens === 'book' && (
          <label className="ctrl" data-fg-tour="firm.focus-ratio">
            Focus ratio
            <select
              value={resolvedFocusId}
              onChange={(e) => {
                setFocusRatioId(e.target.value)
                writeFocusRatio(siteSlug, e.target.value)
              }}
            >
              {Object.entries(
                focusOptions.reduce<Record<string, typeof focusOptions>>((acc, opt) => {
                  ;(acc[opt.category] ||= []).push(opt)
                  return acc
                }, {}),
              ).map(([category, opts]) => (
                <optgroup key={category} label={RATIO_CATEGORY_LABEL[category] || category}>
                  {opts.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
        )}
        {lens === 'book' && (
          <label className="ctrl">
            Sort
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortBy)}>
              <option value="health">Health</option>
              <option value="health_score">Health score</option>
              <option value="health_drop">Biggest drop</option>
              <option value="attention">Attention</option>
              <option value="focus_ratio">{focusLabel}</option>
              <option value="name">Name</option>
            </select>
          </label>
        )}
        {lens === 'book' && expandableIds.length > 0 && (
          <button className="cockpit-expand-all" onClick={toggleExpandAll}>
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </button>
        )}
      </div>

      {reviewNote && (
        <div className="cockpit-note" role="status">
          {reviewNote}
          <button type="button" onClick={() => setReviewNote(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      {/* Legend for the trend columns. The pills encode direction-of-good, which is not guessable
          from the arrow alone — a falling DSO is good news and a falling margin is not. */}
      {lens === 'book' && (
        <div className="cockpit-legend">
          <span className="cl-item">
            <span className="delta-pill up">▲</span> improving
          </span>
          <span className="cl-item">
            <span className="delta-pill down">▼</span> getting worse
          </span>
          <span className="cl-item">
            <span className="delta-pill neutral">▲</span> no better or worse direction
          </span>
          <span className="cl-item">
            <svg className="cl-dash" viewBox="0 0 26 8" aria-hidden="true">
              <line x1="0" y1="4" x2="26" y2="4" stroke="var(--anchor)" strokeOpacity="0.55" strokeWidth="1" strokeDasharray="2 3" />
            </svg>
            benchmark
          </span>
          <span className="cl-hint">Hover any sparkline to read a month.</span>
        </div>
      )}

      <div className="cockpit-list-scroll">
        {/* ---- BOOK (roster) ---- */}
        {lens === 'book' && (
          <div className="roster2">
          <div className="roster2-head">
            <span></span>
            <span>Client</span>
            <span>Health</span>
            <span>Stage</span>
            <span className="r2-num-h">Revenue</span>
            <span className="r2-num-h">Margin</span>
            <span>{focusLabel}</span>
            <span>Owner</span>
            <span></span>
          </div>
          {filtered.map((c, rowIndex) => {
            const mom = revenueMomPct(c)
            const summaryItems = attentionItemsFor(c)
            // Prefer the live-fetched spine once it lands — it carries the full
            // multi-surface list even when the denormalized row is stale.
            const live = liveItems[c.workbook_id]
            const items =
              Array.isArray(live) && live.length >= summaryItems.length ? live : summaryItems
            const liveLoading = live === 'loading'
            const hasItems = items.length > 0
            const isOpen = hasItems && expanded.has(c.workbook_id)
            const analyzed = c.last_analyzed_at != null
            const hasScore = c.health_score != null
            const band = hasScore
              ? gradeBand(c.health_score as number)
              : HEALTH_BAND[c.health] ?? 'none'
            const grade = hasScore ? scoreToGrade(c.health_score as number) : '—'
            const worstSev = items.some((it) => it.severity === 'bad') ? 'crit' : 'warn'
            // The badge counts the client's true issue count even while the
            // drawer only has the fallback item to show.
            const badgeCount = Math.max(items.length, Math.min(c.attention_count || 0, 4))
            const rowInteractive = hasItems || companySelectionEnabled
            const healthTitle = hasScore
              ? `${HEALTH_LABEL[c.health] || c.health} · ${Math.round(c.health_score as number)}/100 (${grade})`
              : HEALTH_LABEL[c.health] || c.health
            return (
              <div className={`co${isOpen ? ' open' : ''}`} key={c.workbook_id}>
                {/* A tour anchor has to resolve to exactly one element, so the per-row anchors
                    below are stamped on the first row only. */}
                <div
                  className={`roster2-row co-row${rowInteractive ? ' clickable' : ''}${selectedWorkbookId === c.workbook_id ? ' selected' : ''}`}
                  ref={selectedWorkbookId === c.workbook_id ? selectedRowRef : undefined}
                  data-fg-tour={rowIndex === 0 ? 'firm.client-row' : undefined}
                  role={rowInteractive ? 'button' : undefined}
                  tabIndex={rowInteractive ? 0 : undefined}
                  aria-current={selectedWorkbookId === c.workbook_id ? 'true' : undefined}
                  aria-expanded={hasItems ? isOpen : undefined}
                  onClick={rowInteractive ? () => {
                    if (companySelectionEnabled) selectCompany(c)
                    if (hasItems) toggleExpanded(c)
                  } : undefined}
                  onKeyDown={rowInteractive ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      if (companySelectionEnabled) selectCompany(c)
                      if (hasItems) toggleExpanded(c)
                    }
                  } : undefined}
                >
                  <div className="r2-chev" aria-hidden="true">
                    {hasItems ? '▶' : ''}
                  </div>
                  <div className="r2-client">
                    <div className="r2-name">{c.client_name}</div>
                    <div className="r2-meta">
                      {c.company_name || 'CFO Core'} · {relTime(c.last_analyzed_at)}
                    </div>
                    <div className="r2-badges">
                      <span className="attn-pill clear">
                        {accountingProviderLabel(c.accounting_provider)}{c.is_demo ? ' sample' : ''}
                        {c.currency_code ? ` · ${c.currency_code}` : ''}
                      </span>
                      {hasItems ? (
                        <span className={`attn-pill ${worstSev}`}>
                          <span className="dot" />
                          {badgeCount} {badgeCount === 1 ? 'item needs' : 'items need'} attention
                        </span>
                      ) : !analyzed ? (
                        <span className="attn-pill stale">
                          <span className="dot" />
                          Not analyzed yet
                        </span>
                      ) : (
                        <span className="attn-pill clear">
                          <span className="dot" />
                          All clear
                        </span>
                      )}
                      {hasItems && (
                        <span className="attn-cue">{isOpen ? 'Hide details ▾' : 'Show details ›'}</span>
                      )}
                    </div>
                  </div>
                  <div className="r2-health" data-fg-tour={rowIndex === 0 ? 'firm.health-score' : undefined}>
                    <div className="hg" title={healthTitle}>
                      <span className={`hg-grade band-${band}`}>{grade}</span>
                      <div className="hg-r">
                        <div className="hg-top">
                          {hasScore ? (
                            <>
                              <span className="hg-score">{Math.round(c.health_score as number)}</span>
                              {c.health_delta_1mo != null && Math.abs(c.health_delta_1mo) >= 0.5 && (
                                <span className="hg-trend" style={{ color: deltaColor(c.health_delta_1mo) }}>
                                  {c.health_delta_1mo >= 0 ? '▲' : '▼'} {formatDelta(c.health_delta_1mo)}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="hg-word">{HEALTH_LABEL[c.health] || c.health}</span>
                          )}
                        </div>
                        {hasScore && (
                          <span className="hg-track">
                            <i
                              className={`band-${band}`}
                              style={{ width: `${Math.max(0, Math.min(100, c.health_score as number))}%` }}
                            />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <CockpitStageCell
                      client={c}
                      busy={reviewing === c.workbook_id}
                      onMarkReviewed={() => markReviewed(c)}
                      onOpenPresentations={() => openClient(c)}
                    />
                  </div>
                  <div className="r2-metric-cell" data-l="Revenue">
                    <CockpitMetricTrendCell
                      display={fmtMoneyCompact(c.revenue, c.currency_code || 'USD')}
                      values={(c.revenue_trend ?? []).map((p) => p.value)}
                      labels={(c.revenue_trend ?? []).map((p) => formatFocusPeriod(p.period))}
                      formatValue={(v) => fmtMoneyCompact(v, c.currency_code || 'USD')}
                      delta={mom}
                      deltaDisplay={mom != null ? `${Math.abs(mom).toFixed(1)}%` : null}
                      higherIsBetter
                      ariaLabel={`Revenue trend for ${c.client_name}`}
                    />
                  </div>
                  <div className="r2-metric-cell" data-l="Margin">
                    <CockpitMetricTrendCell
                      display={fmtPct(c.gross_margin_pct)}
                      values={marginTrend(c).map((p) => p.value)}
                      labels={marginTrend(c).map((p) => formatFocusPeriod(p.period))}
                      formatValue={(v) => `${(v * 100).toFixed(1)}%`}
                      delta={marginMomPts(c)}
                      deltaDisplay={
                        marginMomPts(c) != null ? `${Math.abs(marginMomPts(c) as number).toFixed(1)} pts` : null
                      }
                      higherIsBetter
                      ariaLabel={`Gross margin trend for ${c.client_name}`}
                    />
                  </div>
                  <div className="r2-focus-cell" data-l={focusLabel}>
                    <CockpitFocusRatioCell
                      slice={focusSliceFor(c, resolvedFocusId)}
                      analyzed={analyzed}
                    />
                  </div>
                  <div className="r2-owner" onClick={(e) => e.stopPropagation()}>
                    {members.length > 0 ? (
                      <select
                        value={c.assigned_to_user_id || ''}
                        onChange={(e) => assign(c.workbook_id, e.target.value || null)}
                      >
                        <option value="">Unassigned</option>
                        {members.map((m) => (
                          <option key={m.user_id} value={m.user_id}>
                            {m.email}
                          </option>
                        ))}
                      </select>
                    ) : (
                      // Read-only viewers can't list site members (the dropdown
                      // would render every row as "Unassigned"); show the
                      // assignee from the portfolio payload instead.
                      <span>
                        {c.assigned_to_name ||
                          (c.assigned_to_email ? c.assigned_to_email.split('@')[0] : 'Unassigned')}
                      </span>
                    )}
                  </div>
                  <div className="r2-open">
                    <button
                      data-fg-tour={rowIndex === 0 ? 'firm.open-client' : undefined}
                      onClick={(e) => {
                        e.stopPropagation()
                        openClient(c)
                      }}
                    >
                      Open board report →
                    </button>
                  </div>
                </div>
                {isOpen && (
                  <div
                    className="co-drawer"
                    // Gated on the first row that HAS a drawer, not on row 0: the default sort puts
                    // blocked and unanalyzed clients first, and those carry no attention items, so
                    // a row-0 gate would leave this anchor matching nothing.
                    data-fg-tour={c.workbook_id === expandableIds[0] ? 'firm.attention' : undefined}
                  >
                    <div className="co-drawer-inner">
                      <CockpitClientHighlight
                        api={api}
                        workbookId={c.workbook_id}
                        accountingProvider={c.accounting_provider}
                        reportingPeriod={c.live_board_report?.reporting_period || c.period}
                      />
                      <div className="co-drawer-cap">Top things that need attention</div>
                      {items.map((it, i) => {
                        const dest = drillDestinationLabel(
                          (it.drill ?? null) as InsightDrillTarget | null,
                        )
                        return (
                          <button
                            key={i}
                            className="ai-row"
                            onClick={() => openAttentionItem(c, it)}
                          >
                            <span className={`sev-dot sev-${it.severity || 'watch'}`} />
                            <span className="ai-body">
                              <span className="ai-headline">{it.headline}</span>
                              {it.detail ? <span className="ai-detail">{it.detail}</span> : null}
                            </span>
                            <span className="ai-open">{dest ? `Open ${dest} →` : 'Open →'}</span>
                          </button>
                        )
                      })}
                      {liveLoading && (
                        <div className="ai-loading">Pulling the latest analysis…</div>
                      )}
                      <div className="co-drawer-foot">
                        <span>AI-weighted from the last analyzed close · {relTime(c.last_analyzed_at)}</span>
                        <button className="drawer-full" onClick={() => openFullCfoCore(c)}>
                          Open full CFO Core →
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
          {filtered.length === 0 && (
            <div className="cfo-cockpit-empty">No clients match this filter.</div>
          )}
          </div>
        )}

        {/* ---- BOARD (close pipeline) ---- */}
        {lens === 'board' && (
          <div className="close-board">
          {STAGE_ORDER.map((stage) => {
            const col = filtered.filter((c) => c.close_stage === stage.key)
            // Only "Reviewed" is a real destination: it is the single stage the server can be asked
            // to produce. The rest are outcomes of work that happens elsewhere, so they never light
            // up as drop targets.
            const isDropTarget = stage.key === 'reviewed' && dragging != null
            return (
              <div
                className={`board-col${isDropTarget ? ' droppable' : ''}${
                  dropTarget === stage.key ? ' dropping' : ''
                }`}
                key={stage.key}
                onDragOver={(e) => {
                  if (!isDropTarget) return
                  e.preventDefault()
                  setDropTarget(stage.key)
                }}
                onDragLeave={() => setDropTarget((t) => (t === stage.key ? null : t))}
                onDrop={(e) => {
                  e.preventDefault()
                  setDropTarget(null)
                  if (!isDropTarget || !dragging) return
                  const card = filtered.find((c) => c.workbook_id === dragging)
                  setDragging(null)
                  if (card && canMarkReviewed(card)) markReviewed(card)
                }}
              >
                <div className="board-col-head">
                  {stage.label} <span className="board-count">{col.length}</span>
                </div>
                <div className="board-col-body">
                  {col.map((c) => {
                    const draggable = canMarkReviewed(c)
                    return (
                    <div
                      key={c.workbook_id}
                      role="button"
                      tabIndex={0}
                      className={`board-card${draggable ? ' draggable' : ''}${
                        dragging === c.workbook_id ? ' dragging' : ''
                      }`}
                      draggable={draggable}
                      title={draggable ? 'Drag into Reviewed to record a review' : undefined}
                      onDragStart={() => setDragging(c.workbook_id)}
                      onDragEnd={() => {
                        setDragging(null)
                        setDropTarget(null)
                      }}
                      onClick={() => openClient(c)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          openClient(c)
                        }
                      }}
                    >
                      <div className="bc-top">
                        <span className={`health-dot h-${c.health}`} />
                        <span className="bc-name">{c.client_name}</span>
                      </div>
                      {c.top_flag_headline && <div className="bc-flag">{c.top_flag_headline}</div>}
                      <div className="bc-meta">
                        {c.assigned_to_name ||
                          (c.assigned_to_email ? c.assigned_to_email.split('@')[0] : 'unassigned')}
                        {' · '}
                        {relTime(c.last_analyzed_at)}
                      </div>
                    </div>
                    )
                  })}
                  {col.length === 0 && <div className="board-empty">—</div>}
                </div>
              </div>
            )
          })}
          </div>
        )}

        <div className="cockpit-foot">
          Signals reflect each client’s last analyzed close; a client that hasn’t been opened shows
          “not analyzed yet.” Every number opens the client’s CFO Core at the source.
        </div>
      </div>
     </div>
    </div>
  )
}
